import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { map } from 'rxjs/operators';

import { NavService, routeOf } from '@smw/navigation-front';

import { AuthenticationService } from '../services';
import { CallbackUrlService } from '../services/callback-url.service';

type AnonymousRedirection = InternalAnonymousRedirection | ExternalAnonymousRedirection;

type InternalAnonymousRedirection = {
  type: 'internal';
  url: UrlTree;
};

type ExternalAnonymousRedirection = {
  type: 'external';
  url: string;
};

interface AnonymousRedirectionStrategy {
  getRedirectionFor(route: ActivatedRouteSnapshot): AnonymousRedirection | undefined;
}

@Injectable({ providedIn: 'root' })
export class AnonymousTrainingModuleRedirectionStrategy implements AnonymousRedirectionStrategy {
  getRedirectionFor(route: ActivatedRouteSnapshot): ExternalAnonymousRedirection | undefined {
    const eventCode = route.paramMap.get('id');
    if (!eventCode) {
      return;
    }

    return {
      type: 'external',
      url: `https://somanyways.retool.com/p/feedback?eventCode=${eventCode}`,
    };
  }
}

@Injectable({ providedIn: 'root' })
export class AnonymousRedirectionStrategiesService {
  private strategies = new Map<string, AnonymousRedirectionStrategy>();

  constructor(trainingModuleStrategy: AnonymousTrainingModuleRedirectionStrategy) {
    this.strategies.set(routeOf('collaborator.trainings.module'), trainingModuleStrategy);
  }

  getRedirectionFor(route: ActivatedRouteSnapshot): AnonymousRedirection | undefined {
    const path = routeOf('collaborator.trainings.module');
    if (!this.strategies.has(path)) {
      return;
    }

    const strategy = this.strategies.get(path);
    return strategy?.getRedirectionFor(route);
  }
}

function getDeepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let candidate = route;

  while (candidate.firstChild) {
    candidate = candidate.firstChild;
  }
  return candidate;
}

export const loggedInGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const router = inject(Router);
  const nav = inject(NavService);
  const authService = inject(AuthenticationService);
  const callbackUrlService = inject(CallbackUrlService);
  const redirectionStrategies = inject(AnonymousRedirectionStrategiesService);

  return authService.isAuthenticated().pipe(
    map((isAuthenticated) => {
      if (isAuthenticated) {
        return true;
      }

      const deepestRoute = getDeepestRoute(route);
      const redirection = redirectionStrategies.getRedirectionFor(deepestRoute);
      if (redirection) {
        switch (redirection.type) {
          case 'internal':
            return redirection.url;
          case 'external':
            window.location.href = redirection.url;
            return false;
          default:
          // do nothing
        }
      }

      callbackUrlService.register(state.url);
      return router.createUrlTree([nav.routeOf('auth')], {
        queryParamsHandling: 'merge',
        queryParams: route.queryParams,
      });
    }),
  );
};
